import { useRef, useState, useEffect } from "react";
import { Record, Stop, Play, Pause } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";

export const VoiceRecorderButton = ({ onRecorded, disabled }) => {
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size > 0) onRecorded(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Please allow microphone access");
    }
  };

  const stop = () => {
    mediaRef.current?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  return (
    <button type="button" data-testid="voice-record-button" disabled={disabled} onClick={recording ? stop : start}
      title="Record voice message"
      className={`shrink-0 rounded-md p-2 flex items-center gap-1 transition-all active:scale-90 ${recording ? "bg-red-500 text-white" : "bg-muted hover:bg-red-100 text-red-600"}`}>
      {recording ? <><Stop size={18} weight="fill" /><span className="font-mono text-xs">{seconds}s</span></> : <Record size={18} weight="fill" />}
    </button>
  );
};

export const VoicePlayer = ({ url }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [src, setSrc] = useState(null);

  const toggle = async () => {
    if (!src) {
      try {
        const r = await api.get(url, { responseType: "blob" });
        const u = URL.createObjectURL(r.data);
        setSrc(u);
        setTimeout(() => audioRef.current?.play(), 100);
        setPlaying(true);
      } catch {
        toast.error("Could not load the voice message");
      }
      return;
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <button type="button" data-testid="voice-play-button" onClick={toggle}
        className="bg-primary/20 hover:bg-primary/40 text-foreground rounded-full p-1.5 active:scale-90 transition-transform">
        {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
      </button>
      <span className="text-xs">🎤 Voice message</span>
      {src && <audio ref={audioRef} src={src} onEnded={() => setPlaying(false)} className="hidden" />}
    </span>
  );
};
